import { max as dateMax, min as dateMin } from 'date-fns';
import { min, max } from 'lodash-es';

import {
  Aggregation,
  AggregationOptions,
  CatalogApiOptions,
  ChangeDetector,
  DcuplFilterOptions,
  DcuplFiltersOptions,
  DcuplGroupByOptions,
  DcuplItemsOptions,
  DcuplQueryBuilder,
  DcuplQueryGroup,
  FilterComparisonType,
  FilterItem,
  FilterItemDateRangeValue,
  FilterItemEntry,
  FilterItemNumberRangeValue,
  FilterItemType,
  FilterStringArray,
  FilterStringSingle,
  getFacets,
  InverseIndexValuesMap,
  ListItem,
  PropertyFilterItem,
  PropertyType,
  ReferenceFilterItem,
  SectionItem,
  SortingProjection,
  TextFilterItem,
  getAggregation,
  IndicesController,
  CacheController,
  GroupByResponse,
  QueryManager,
  perf,
} from '@dcupl/common';

import { DcuplModel, ModelParser } from '../../model-parser';
import { ItemService } from '../item/item';

export class Filter<ListType extends ListItem = ListItem> {
  public initialData!: Map<string, ListType>;
  public filteredData!: Map<string, ListType>;
  public model!: DcuplModel<ListType>;
  public filterItems: Map<string, FilterItem> = new Map();
  public queryBuilder!: DcuplQueryBuilder;

  public cacheCtrl!: CacheController;

  constructor(
    public modelParser: ModelParser,
    public cdRef: ChangeDetector,
    public item: ItemService,
    public indicesController: IndicesController,
    public queryManager: QueryManager,
  ) {
    this.cacheCtrl = new CacheController(!!this.cdRef.dcuplInitOptions.performance?.cache?.enabled);
  }

  public init(model: DcuplModel<ListType>, data: Map<string, ListType>): void {
    this.model = model;
    this.initialData = data;
    this.filteredData = this.initialData;

    this.queryBuilder = new DcuplQueryBuilder();
    this.queryBuilder.init({ modelKey: model.key });

    this.initFilterItems({ skipProcessing: true });
  }

  public update(model: DcuplModel<ListType>, data: Map<string, ListType>, indicesController: IndicesController): void {
    this.model = model;
    this.initialData = data;
    this.indicesController = indicesController;
    this.cacheCtrl.clear();
    this.filteredData = this.getFilteredData(this.initialData, false);
    this.initFilterItems({ skipProcessing: true });
  }

  public resetCache(): void {
    this.cacheCtrl.clear();
  }

  private initFilterItems(options?: DcuplFiltersOptions): void {
    const attributes = [...this.model.properties.values(), ...this.model.references.values()];

    attributes.forEach((attribute) => {
      if (attribute.filter) {
        if (Array.isArray(options?.filterKeys)) {
          if (options?.filterKeys.includes(attribute.key)) {
            this.updateFilterItem(attribute.key, options);
          }
        } else {
          this.updateFilterItem(attribute.key, options);
        }
      }
    });
  }

  public getAggregate(
    options: AggregationOptions,
    relevantData: Map<string, ListType>,
    indexMap: InverseIndexValuesMap,
  ): Aggregation {
    const attribute = this.model.getAttribute(options.attribute);

    if (!attribute) {
      throw new Error('Attribute not found');
    }

    return getAggregation(options, relevantData, indexMap, attribute.key);
  }

  public getSections(options: DcuplGroupByOptions<any>): GroupByResponse {
    const response: GroupByResponse = {
      _meta: {
        currentSize: 0,
        initialSize: 0,
      },
      items: [],
    };

    const attribute = this.model.getAttribute(options.attribute);

    if (!attribute) {
      throw new Error('Attribute not found');
    }

    let sectionItems: Map<string, SectionItem> = new Map();

    let remoteModel: DcuplModel | undefined;

    if (attribute.type === 'singleValued' || attribute.type === 'multiValued') {
      remoteModel = this.modelParser.models.get(attribute.model)!;
    }

    const childLevelOptions: DcuplItemsOptions<any> = {};

    const childLevelSorting: SortingProjection<any> = this.getSortingProjectionForModel(options, this.model);
    childLevelOptions.sort = childLevelSorting;

    // 1) PREPARE INITIAL DATA
    // get the filteredData only SORTED

    const relevantData = this.item.getItemsAsMap(this.filteredData, this.model, childLevelOptions) as Map<
      string,
      ListType
    >;

    const indexMap = this.indicesController.getOrCreateIndex(`${options.attribute}`, relevantData, {
      childLevelSorting,
      query: this.queryBuilder.getQuery(),
    });

    // 2) get the distinct values for the attribute

    // todo distinctoptions
    const agg = this.getAggregate(
      { attribute: options.attribute, types: ['distinct'], excludeUndefineds: true },
      relevantData,
      indexMap,
    );

    if (agg.distinct) {
      for (const distinctAggValue of agg.distinct) {
        let item: ListItem = { key: distinctAggValue.value };

        const values = distinctAggValue.keys!.filter((key) => relevantData.has(key));

        if (remoteModel) {
          const remoteItem = remoteModel['data'].get(distinctAggValue.value);
          if (remoteItem) {
            item = remoteItem;
          }
        }
        const sectionItem: SectionItem = {
          ...item,
          key: distinctAggValue.value,
          _items: [],
          _aggregates: [],
          _meta: {
            size: values.length,
          },
        };

        sectionItems.set(distinctAggValue.value, sectionItem);
      }
    }

    // // 3) Page, Sort and project the section data
    const topLevelOptions: DcuplItemsOptions<any> = {
      count: options.count,
      start: options.start,
      projection: options.projection,
    };
    if (remoteModel) {
      const topLevelSorting: SortingProjection<any> = this.getSortingProjectionForModel(options, remoteModel);
      topLevelOptions.sort = topLevelSorting;
    }

    response._meta.currentSize = sectionItems.size;
    response._meta.initialSize = agg.distinct!.length;

    sectionItems = this.item.getItemsAsMap(sectionItems, remoteModel, topLevelOptions) as Map<string, SectionItem>;

    if (Array.isArray(options?.aggregates)) {
      sectionItems.forEach((sectionItem) => {
        const initialAggregate = agg.distinct!.find((d) => d.value === sectionItem.key);
        const filteredData = this.getDataByKeys(relevantData, initialAggregate!.keys!);
        sectionItem._aggregates = options.aggregates!.map((aggOption) => {
          return this.getAggregate(aggOption, filteredData, indexMap as InverseIndexValuesMap);
        });
      });
    }

    if (options?.items) {
      const wildCardItem = options.items?.find((i) => i.key === '*');

      if (wildCardItem) {
        sectionItems.forEach((sectionItem) => {
          const initialAggregate = agg.distinct!.find((d) => d.value === sectionItem.key);
          const filteredData = this.getDataByKeys(relevantData, initialAggregate!.keys!);
          const items = this.item.getItems(filteredData, this.model, wildCardItem as DcuplItemsOptions<any>);
          sectionItem._items = items;
        });
      } else {
        sectionItems.forEach((sectionItem) => {
          const optionItem = options.items?.find((i) => i.key === sectionItem.key);
          if (optionItem) {
            const initialAggregate = agg.distinct!.find((d) => d.value === sectionItem.key);
            const filteredData = this.getDataByKeys(relevantData, initialAggregate!.keys!);
            const items = this.item.getItems(filteredData, this.model, optionItem as DcuplItemsOptions<any>);
            sectionItem._items = items;
          }
        });
      }
    }

    response.items = Array.from(sectionItems.values());
    return response;
  }

  private getSortingProjectionForModel(
    options: DcuplGroupByOptions<any>,
    model: DcuplModel<ListItem>,
  ): SortingProjection<any> {
    const topLevelSorting: SortingProjection<any> = {
      attributes: [],
      order: [],
    };

    options?.sort?.attributes.forEach((attribute, idx) => {
      if (model.properties.has(attribute as string)) {
        topLevelSorting.attributes.push(attribute);
        const orders = options.sort?.order as [];
        topLevelSorting.order.push(orders[idx]!);
      }
    });
    return topLevelSorting;
  }

  public getReferenceKeyForFilterKey(filterKey: string): string {
    const filterItem = this.filterItems.get(filterKey);
    return filterItem?.attributeKey || filterItem?.key || filterKey;
  }

  public updateFilterItem(filterKey: string, options: DcuplFilterOptions = {}): void {
    const defaultOptions: DcuplFilterOptions = {
      calculateFacets: false,
      excludeZeros: true,
      excludeUndefineds: true,
      excludeUnresolved: true,
      skipProcessing: false,
    };

    const referenceKey = this.getReferenceKeyForFilterKey(filterKey);

    if (!referenceKey) {
      return;
    }

    const reference = this.model.references.get(referenceKey);

    let relevantOptions: DcuplFilterOptions = {};
    if (typeof reference?.filter === 'object') {
      relevantOptions = Object.assign(defaultOptions, reference.filter, options);
    } else {
      relevantOptions = Object.assign(defaultOptions, options);
    }

    if (reference) {
      this.updateFilterItemReference(reference.key, reference.key, relevantOptions);
      return;
    }

    const property = this.model.properties.get(referenceKey);

    if (property) {
      if (Array.isArray(property.filter)) {
        property.filter.forEach((filter) => {
          const comparisonType = (filter as FilterStringArray)?.comparisonType || 'containsIgnoreCase';
          this.updateFilterItemProperty(filter.key, property.key, property.type, comparisonType, relevantOptions);
        });
        return;
      } else {
        const comparisonType = (property.filter as FilterStringSingle)?.comparisonType || 'containsIgnoreCase';
        this.updateFilterItemProperty(property.key, property.key, property.type, comparisonType, relevantOptions);
        return;
      }
    }

    if (filterKey === 'key') {
      const comparisonType = 'containsIgnoreCase';
      this.updateFilterItemProperty('key', 'key', 'string', comparisonType, relevantOptions);
      return;
    }
  }

  private updateFilterItemReference(filterKey: string, attributeKey: string, options: DcuplFilterOptions): void {
    let entries: FilterItemEntry[] = [];
    const reference = this.model.references.get(attributeKey);
    if (reference && !options.skipProcessing) {
      const remoteModel = this.modelParser.models.get(reference.model);
      entries = this.getFacets(reference.key, remoteModel, options);
    }
    const filterItem = {
      key: filterKey,
      attributeKey: attributeKey,
      enabled: !!entries.find((entry) => entry.enabled),
      selected: !!entries.find((entry) => entry.selected),
      type: 'reference',
      entries: entries,
    } as ReferenceFilterItem;

    this.filterItems.set(filterItem.key, filterItem);
  }

  private updateFilterItemProperty(
    filterKey: string,
    attributeKey: string,
    type: PropertyType,
    comparisonType: FilterComparisonType,
    options: DcuplFilterOptions,
  ): void {
    const filterType = this.getFilterTypeForPropertyType(type);

    const hasFilterEntry = this.queryBuilder.hasQueryGroup(filterKey);

    const filterItem = {
      key: filterKey,
      attributeKey: attributeKey,
      enabled: true,
      selected: !!hasFilterEntry,
      type: filterType,
    } as PropertyFilterItem;

    if (filterItem.type === 'text') {
      (filterItem as TextFilterItem).comparisonType = comparisonType;
    }

    if (filterItem.type === 'boolean') {
      const entries = this.getFacets(filterItem.attributeKey, undefined, options);
      filterItem.entries = entries;
    }

    if (!options.skipProcessing) {
      if (filterItem.type === 'number') {
        filterItem.range = this.getNumberRangesForFilterItem(filterItem);
      }
      if (filterItem.type === 'date') {
        filterItem.range = this.getDateRangesForFilterItem(filterItem);
      }
    }
    this.filterItems.set(filterItem.key, filterItem);
  }

  public getNumberRange(propertyKey: string, filterKey = propertyKey): FilterItemNumberRangeValue {
    const filteredData = Array.from(this.filteredData.values());
    const filteredValues: number[] = filteredData
      .map((item) => item[propertyKey])
      .filter((item) => typeof item !== 'undefined' && item !== null);
    const flatFilteredValues = filteredValues.flat();

    const filteredMin = min(flatFilteredValues) as number;
    const filteredMax = max(flatFilteredValues) as number;

    const initialData = Array.from(this.initialData.values());
    const initialValues: number[] = initialData
      .map((item) => item[propertyKey])
      .filter((item) => typeof item !== 'undefined' && item !== null);

    const flatInitialValues = initialValues.flat();
    const initialMin = min(flatInitialValues) as number;
    const initialMax = max(flatInitialValues) as number;

    const ranges: FilterItemNumberRangeValue = {
      available: {
        min: filteredMin,
        max: filteredMax,
      },
      initial: {
        min: initialMin,
        max: initialMax,
      },
      relatedQuery: this.queryBuilder.getQueryGroup(filterKey),
    };

    return ranges;
  }

  private getNumberRangesForFilterItem(filterItem: FilterItem): FilterItemNumberRangeValue {
    const ranges = this.getNumberRange(filterItem.attributeKey, filterItem.key);
    return ranges;
  }

  private getDateRangesForFilterItem(filterItem: FilterItem): FilterItemDateRangeValue {
    const ranges: FilterItemDateRangeValue = this.getDateRange(filterItem.attributeKey, filterItem.key);
    return ranges;
  }

  public getDateRange(propertyKey: string, filterKey = propertyKey): FilterItemDateRangeValue {
    const filteredData = Array.from(this.filteredData.values());
    const filteredValues: Date[] = filteredData
      .map((item) => item[propertyKey])
      .filter((item) => typeof item !== 'undefined' && item !== null);
    const flatFilteredValues = filteredValues.flat();

    const filteredMin = dateMin(flatFilteredValues);
    const filteredMax = dateMax(flatFilteredValues);

    const initialData = Array.from(this.initialData.values());
    const initialValues: Date[] = initialData
      .map((item) => item[propertyKey])
      .filter((item) => typeof item !== 'undefined' && item !== null);
    const flatInitialValues = initialValues.flat();
    const initialMin = dateMin(flatInitialValues);
    const initialMax = dateMax(flatInitialValues);

    const ranges: FilterItemDateRangeValue = {
      available: {
        min: filteredMin,
        max: filteredMax,
      },
      initial: {
        min: initialMin,
        max: initialMax,
      },
      relatedQuery: this.queryBuilder.getQueryGroup(filterKey),
    };

    return ranges;
  }

  public getFacets(
    attributeKey: string,
    remoteModel: DcuplModel | undefined,
    options: DcuplFilterOptions,
  ): FilterItemEntry[] {
    let indexMap: InverseIndexValuesMap;

    const cacheContext = this.cacheCtrl.generateContext({ attributeKey, query: this.queryBuilder.getQuery(), options });

    const cachedValue = this.cacheCtrl.get('facets', cacheContext);

    if (cachedValue) {
      return cachedValue;
    }

    let remoteData: Map<string, ListItem> = new Map();

    indexMap = this.indicesController.getIndex(attributeKey)!;

    if (remoteModel) {
      remoteData = remoteModel.getData();
    } else {
      remoteData = this.indicesController.getData(attributeKey)!;
    }
    // Autogenerate index if not present
    if (!indexMap || !remoteData) {
      this.indicesController.reset(attributeKey);
      this.initialData.forEach((entry) => {
        const attributeValue = entry[attributeKey];
        this.indicesController.fillInverseIndex(attributeKey, entry.key, [attributeValue]);
        this.indicesController.fillDataMap(attributeKey, [attributeValue]);
      });

      indexMap = this.indicesController.getIndex(attributeKey)!;
      remoteData = this.indicesController.getData(attributeKey)!;
    }

    const relevantModelData = this.initialData;

    const facets = getFacets(
      relevantModelData,
      remoteData,
      options,
      this.queryBuilder.getQuery(),
      attributeKey,
      this.indicesController['indexMap'],
      this.queryManager,
    );
    this.cacheCtrl.set('facets', cacheContext, facets);
    return facets;
  }

  public getDataByKeys(data: Map<string, ListType>, itemKeys: string[]): Map<string, ListType> {
    if (itemKeys) {
      const filteredData = new Map();
      itemKeys.forEach((key) => {
        const entry = data.get(key);
        if (entry) {
          filteredData.set(entry.key, entry);
        }
      });
      return filteredData;
    }
    return data;
  }

  private getFilterTypeForPropertyType(type: PropertyType): FilterItemType {
    switch (type) {
      case 'string':
        return 'text';
      case 'int':
        return 'number';
      case 'float':
        return 'number';
      case 'date':
        return 'date';
      case 'boolean':
        return 'boolean';
      case 'json':
        return 'text';
      case 'any':
        return 'text';
      case 'Array<string>':
        return 'text';
      case 'Array<int>':
        return 'number';
      case 'Array<float>':
        return 'number';
      case 'Array<date>':
        return 'date';
    }
  }

  public updateFilteredData(options?: CatalogApiOptions): void {
    const start = perf.now();
    if (!options?.skipProcessing) {
      this.filteredData = this.getFilteredData(this.initialData, false);

      this.cdRef.trigger({
        scope: 'list',
        type: 'result_updated',
        action: 'update',
        connectMessage: true,
        listKey: this.cdRef.key,
        modelKey: this.model.key,
        start,
      });
    }
  }

  public getFilteredData(initialData: Map<string, ListType>, earlyReturn: boolean): Map<string, ListType> {
    const cacheContext = this.cacheCtrl.generateContext({ query: this.queryBuilder.getQuery(), earlyReturn });

    const cachedValue = this.cacheCtrl.get('data', cacheContext);

    if (cachedValue) {
      return cachedValue;
    }

    const query = this.queryBuilder.getQuery();
    const value = this.queryManager.queryData(
      initialData,
      query as unknown as DcuplQueryGroup,
      earlyReturn,
      this.indicesController['indexMap'],
    );

    this.cacheCtrl.set('data', cacheContext, value);
    return value;
  }

  public getFilterItems(options?: DcuplFiltersOptions): FilterItem[] {
    if (Array.isArray(options?.filterKeys)) {
      const items: FilterItem[] = [];
      options?.filterKeys.forEach((filterKey) => {
        const item = this.getFilterItem(filterKey, options);
        if (item) {
          items.push(item);
        }
      });
      return items;
    } else {
      if (!options?.skipProcessing) {
        this.initFilterItems(options);
      }
      return Array.from(this.filterItems.values());
    }
  }

  public getFilterItem(filterItemId: string, options?: DcuplFilterOptions): FilterItem | undefined {
    if (this.filterItems.has(filterItemId)) {
      if (!options?.skipProcessing) {
        this.updateFilterItem(filterItemId, options);
      }
    }

    return this.filterItems.get(filterItemId);
  }
}
